import { useCallback } from 'react';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '@forvalter/store/hooks';

import { Portfolio } from './classes/Portfolio';
import { Region } from './classes/Region';
import type {
    IPortfolioState,
    IPortfolioStore,
    IRegionState,
    IRegionStore,
} from './interfaces';

/* State */
export const portfolioInitialState = (): IPortfolioState => ({
    portfolio: Portfolio.storedSelectedPortfolio,
});

export const regionInitialState = (): IRegionState => ({
    region: Region.storedSelectedRegion,
});

/* Selectors */
export const portfolioSelectors = ({
    portfolio,
}: IPortfolioStore): IPortfolioState => portfolio;

export const regionSelectors = ({ region }: IRegionStore): IRegionState =>
    region;

/* Portfolio Slice */
const portfolioSlice = createSlice({
    name: 'portfolio',
    initialState: portfolioInitialState(),
    reducers: {
        portfolioSelect: (state, action: PayloadAction<Portfolio>) => {
            state.portfolio = action.payload;
            // Save to localStorage
            Portfolio.storedSelectedPortfolio = action.payload;
        },
        portfolioResetField: (state) => {
            state.portfolio = undefined;
            // Clear from localStorage
            Portfolio.storedSelectedPortfolio = Portfolio.empty();
        },
        portfolioReset: () => {
            // Clear from localStorage
            Portfolio.storedSelectedPortfolio = Portfolio.empty();
            return portfolioInitialState();
        },
    },
});

const { portfolioSelect, portfolioResetField, portfolioReset } =
    portfolioSlice.actions;

export const portfolioReducer = portfolioSlice.reducer;

/* Region Slice */
const regionSlice = createSlice({
    name: 'region',
    initialState: regionInitialState(),
    reducers: {
        regionSelect: (state, action: PayloadAction<Region>) => {
            state.region = action.payload;
            // Save to localStorage
            Region.storedSelectedRegion = action.payload;
        },
        regionResetField: (state) => {
            state.region = undefined;
            // Clear from localStorage
            Region.storedSelectedRegion = Region.empty();
        },
        regionReset: () => {
            // Clear from localStorage
            Region.storedSelectedRegion = Region.empty();
            return regionInitialState();
        },
    },
});

const { regionSelect, regionResetField, regionReset } = regionSlice.actions;

export const regionReducer = regionSlice.reducer;

/* Hooks */
export const usePortfolio = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(portfolioSelectors);
    // Stable, so that they can be dependencies of effects
    const selectPortfolio = useCallback(
        (portfolio: Portfolio) => dispatch(portfolioSelect(portfolio)),
        [dispatch],
    );
    const resetPortfolio = useCallback(
        () => dispatch(portfolioReset()),
        [dispatch],
    );
    const resetPortfolioField = useCallback(
        () => dispatch(portfolioResetField()),
        [dispatch],
    );
    return { ...state, selectPortfolio, resetPortfolio, resetPortfolioField };
};

export const useRegion = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(regionSelectors);
    // Stable, so that they can be dependencies of effects
    const selectRegion = useCallback(
        (region: Region) => dispatch(regionSelect(region)),
        [dispatch],
    );
    const resetRegion = useCallback(() => dispatch(regionReset()), [dispatch]);
    const resetRegionField = useCallback(
        () => dispatch(regionResetField()),
        [dispatch],
    );
    return { ...state, selectRegion, resetRegion, resetRegionField };
};
